import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaMapMarkerAlt, FaSearch, FaSpinner, FaTimes, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { searchLocation } from '../api/client';

/**
 * LocationInput - Enhanced location input with search and selection
 *
 * Features:
 * - Debounced search as user types
 * - Exact match detection (prioritized)
 * - Multiple result selection when ambiguous
 * - Visual feedback for match quality
 */
const LocationInput = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter a location...",
  label,
  icon: Icon = FaMapMarkerAlt,
  iconColor = "text-primary-500",
  error,
  disabled = false,
  className = "",
  country = null, // Optional country filter
  state = null,   // Optional state filter
  required = false,
  id,
  voiceInput = null, // Optional VoiceInputInline component
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [hasExactMatch, setHasExactMatch] = useState(false);

  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await searchLocation(query, state, country, 10);

      setResults(response.results || []);
      setSearchMessage(response.message || '');
      setHasExactMatch(response.exact_match || false);
      setShowResults(true);

      // If there's exactly one exact match, auto-select it
      if (response.exact_match && response.results?.length === 1) {
        const exactResult = response.results[0];
        handleSelectLocation(exactResult);
      }
    } catch (err) {
      console.error('Location search error:', err);
      setResults([]);
      setSearchMessage('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [state, country]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedLocation(null);
    onChange?.(newValue);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 500); // 500ms debounce
  };

  // Handle location selection
  const handleSelectLocation = (location) => {
    const displayName = `${location.name}${location.state ? `, ${location.state}` : ''}${location.country ? `, ${location.country}` : ''}`;
    setInputValue(displayName);
    setSelectedLocation(location);
    setShowResults(false);
    onChange?.(displayName);
    onSelect?.(location);
  };

  // Clear selection
  const handleClear = () => {
    setInputValue('');
    setSelectedLocation(null);
    setResults([]);
    setShowResults(false);
    onChange?.('');
    onSelect?.(null);
    inputRef.current?.focus();
  };

  // Handle voice input
  const handleVoiceTranscript = (text) => {
    setInputValue(text);
    onChange?.(text);
    performSearch(text);
  };

  // Handle focus
  const handleFocus = () => {
    if (results.length > 0 && !selectedLocation) {
      setShowResults(true);
    }
  };

  // Get match type badge color
  const getMatchBadgeClass = (matchType) => {
    return matchType === 'exact'
      ? 'bg-green-100 text-green-700'
      : 'bg-yellow-100 text-yellow-700';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2"
        >
          <Icon className={iconColor} />
          {label}
          {required && <span className="text-red-500">*</span>}
          {selectedLocation && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-green-600 bg-green-100">
              <FaCheckCircle className="text-xs" />
              Verified
            </span>
          )}
        </label>
      )}

      {/* Input Container */}
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            id={id}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full px-4 py-3 pr-20 rounded-lg border-2 transition-all outline-none ${
              error
                ? 'border-red-500 bg-red-50'
                : selectedLocation
                  ? 'border-green-500 bg-green-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          />

          {/* Search/Loading/Clear indicator */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {isSearching && (
              <FaSpinner className="text-primary-400 animate-spin" />
            )}
            {!isSearching && inputValue && (
              <button
                type="button"
                onClick={handleClear}
                className="text-neutral-400 hover:text-red-500 p-1 transition-colors"
                title="Clear"
              >
                <FaTimes />
              </button>
            )}
            {!isSearching && !inputValue && (
              <FaSearch className="text-neutral-300" />
            )}
          </div>
        </div>

        {/* Voice Input */}
        {voiceInput && React.cloneElement(voiceInput, {
          onTranscript: handleVoiceTranscript,
          disabled: disabled,
        })}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}

      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div
          ref={resultsRef}
          className="absolute z-50 w-full mt-1 bg-white border-2 border-neutral-200 rounded-lg shadow-elevated max-h-80 overflow-y-auto"
        >
          {/* Results Header */}
          {searchMessage && (
            <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 text-sm text-neutral-600">
              {hasExactMatch ? (
                <span className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  {searchMessage}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FaExclamationTriangle className="text-amber-500" />
                  {searchMessage}
                </span>
              )}
            </div>
          )}

          {/* Results List */}
          {results.map((result, index) => (
            <button
              key={`${result.lat}-${result.lng}-${index}`}
              type="button"
              onClick={() => handleSelectLocation(result)}
              className="w-full px-4 py-3 text-left hover:bg-primary-50 border-b border-neutral-100 last:border-b-0 transition-colors focus:bg-primary-50 focus:outline-none"
            >
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt className={`mt-1 flex-shrink-0 ${
                  result.match_type === 'exact' ? 'text-green-500' : 'text-amber-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-800 truncate">
                      {result.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getMatchBadgeClass(result.match_type)}`}>
                      {result.match_type === 'exact' ? 'Exact Match' : 'Partial Match'}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 truncate mt-0.5">
                    {result.display_name}
                  </p>
                  {/* Additional context */}
                  <div className="flex items-center gap-2 mt-1 text-xs text-neutral-400">
                    {result.city && <span>{result.city}</span>}
                    {result.state && <span>{result.city ? '•' : ''} {result.state}</span>}
                    {result.country && <span>• {result.country}</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showResults && results.length === 0 && !isSearching && inputValue.length >= 3 && (
        <div
          ref={resultsRef}
          className="absolute z-50 w-full mt-1 bg-white border-2 border-neutral-200 rounded-lg shadow-elevated p-4 text-center"
        >
          <FaExclamationTriangle className="text-amber-400 text-2xl mx-auto mb-2" />
          <p className="text-neutral-600 text-sm">
            No locations found matching "{inputValue}"
          </p>
          <p className="text-neutral-400 text-xs mt-1">
            Try a different search term or be more specific
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationInput;
